/**
 * Файловое поле карточки строки раздела «Экология» (ecology-card-file-fields, файл заказчика
 * 23.09.2026): `file` — один файл (новая загрузка заменяет прежний), `file_multi` — список до
 * `ECO_FIELD_FILES_MAX` файлов (например, протоколы замеров). Файл уходит на сервер сразу после
 * выбора — отдельно от сохранения карточки; прикреплённые файлы видны и в «Документах».
 *
 * `disabled` — просмотр: ссылки на скачивание остаются, действий нет.
 */
import { useRef, useState } from 'react'
import { Paperclip, Trash2, Upload } from 'lucide-react'
import type { EcoCardField, EcoFieldFile, EcoOrderField } from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { ECO_FIELD_FILES_MAX, ECO_FILE_ACCEPT, ECO_FILE_MAX_BYTES } from '@/lib/ecology'
import { Field } from '@/design-system/components/primitives/field'
import { Button } from '@/design-system/components/primitives/button'
import { SingleDocumentUpload } from '@/design-system/components/data/single-document-upload'

/** Файловое поле карточки или заявки (ecology-order-fields-files-fkko): подпись, подсказка и тип. */
type FileFieldLike = Pick<EcoCardField, 'label' | 'hint'> & {
  type: EcoCardField['type'] | EcoOrderField['type']
}

const TOO_LARGE = `Размер файла не должен превышать ${ECO_FILE_MAX_BYTES / (1024 * 1024)} МБ`

export function FieldFiles({
  field,
  files,
  disabled,
  onUpload,
  onDelete,
}: {
  field: FileFieldLike
  files: EcoFieldFile[]
  disabled: boolean
  onUpload: (file: File) => Promise<void>
  onDelete: (fileId: string) => Promise<void>
}) {
  if (field.type === 'file') {
    const current = files[0]
    return (
      <Field label={field.label} hint={field.hint || undefined}>
        {(props) => (
          <SingleDocumentUpload
            id={props.id}
            ariaDescribedBy={props['aria-describedby']}
            inputAriaLabel={field.label}
            file={current ? { name: current.name, downloadUrl: current.url } : null}
            accept={ECO_FILE_ACCEPT}
            maxSizeBytes={ECO_FILE_MAX_BYTES}
            disabled={disabled}
            onUpload={onUpload}
            onDelete={current ? () => onDelete(current.id) : undefined}
          />
        )}
      </Field>
    )
  }
  return (
    <MultiFiles
      field={field}
      files={files}
      disabled={disabled}
      onUpload={onUpload}
      onDelete={onDelete}
    />
  )
}

/** Поле «несколько файлов»: список со ссылками и удалением, «Добавить файлы» — выбор нескольких. */
function MultiFiles({
  field,
  files,
  disabled,
  onUpload,
  onDelete,
}: {
  field: FileFieldLike
  files: EcoFieldFile[]
  disabled: boolean
  onUpload: (file: File) => Promise<void>
  onDelete: (fileId: string) => Promise<void>
}) {
  const inputRef = useRef<HTMLInputElement>(null)
  // Идёт загрузка или удаление: действия поля недоступны до ответа сервера.
  const [status, setStatus] = useState<'uploading' | 'deleting' | null>(null)
  const busy = status !== null
  const [error, setError] = useState<string | null>(null)
  const room = ECO_FIELD_FILES_MAX - files.length

  async function upload(selected: File[]) {
    // Проверки до загрузки: слишком большой файл или лишние файлы — не загружаем ничего.
    if (selected.some((f) => f.size > ECO_FILE_MAX_BYTES)) {
      setError(TOO_LARGE)
      return
    }
    if (selected.length > room) {
      setError(`Можно прикрепить не больше ${ECO_FIELD_FILES_MAX} файлов — осталось ${room}`)
      return
    }
    setError(null)
    setStatus('uploading')
    try {
      // По одному: каждый ответ — свежий обзор, порядок файлов — порядок выбора.
      for (const f of selected) await onUpload(f)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось загрузить файл'))
    } finally {
      setStatus(null)
    }
  }

  async function remove(fileId: string) {
    setError(null)
    setStatus('deleting')
    try {
      await onDelete(fileId)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось удалить файл'))
    } finally {
      setStatus(null)
    }
  }

  return (
    <Field label={field.label} hint={field.hint || undefined}>
      {(props) => (
        <div className="flex flex-col items-start gap-1.5">
          <input
            ref={inputRef}
            id={props.id}
            type="file"
            multiple
            accept={ECO_FILE_ACCEPT}
            aria-label={field.label}
            aria-describedby={props['aria-describedby']}
            className="hidden"
            disabled={disabled || busy || room <= 0}
            onChange={(e) => {
              const selected = Array.from(e.target.files ?? [])
              // Сброс значения — чтобы повторный выбор тех же файлов снова вызвал onChange.
              e.target.value = ''
              if (selected.length > 0) void upload(selected)
            }}
          />
          {files.length > 0 ? (
            <ul className="flex w-full flex-col gap-1">
              {files.map((f) => (
                <li key={f.id} className="flex min-w-0 items-center gap-2">
                  <Paperclip
                    className="size-4 shrink-0 stroke-current text-txt-tertiary"
                    aria-hidden
                  />
                  <a
                    className="min-w-0 break-words text-12 text-accent underline"
                    href={f.url}
                    download
                  >
                    {f.name}
                  </a>
                  {disabled ? null : (
                    <Button
                      type="button"
                      size="sm"
                      variant="secondary"
                      className="ml-auto shrink-0"
                      aria-label={`Удалить «${f.name}»`}
                      title="Удалить файл"
                      disabled={busy}
                      onClick={() => void remove(f.id)}
                    >
                      <Trash2 className="size-3.5 stroke-current" aria-hidden />
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          ) : disabled ? (
            <span className="text-12 text-txt-tertiary">Файлы не прикреплены</span>
          ) : null}
          {busy ? (
            <span className="text-12 text-txt-secondary">
              {status === 'uploading' ? 'Загрузка…' : 'Удаление…'}
            </span>
          ) : null}
          {error ? <span className="text-12 text-red">{error}</span> : null}
          {disabled || room <= 0 ? null : (
            <Button
              type="button"
              size="sm"
              variant="secondary"
              disabled={busy}
              onClick={() => inputRef.current?.click()}
              leftIcon={<Upload className="size-3.5 stroke-current" aria-hidden />}
            >
              {files.length > 0 ? 'Добавить файлы' : 'Загрузить файлы'}
            </Button>
          )}
          {!disabled && room <= 0 ? (
            <span className="text-12 text-txt-tertiary">
              Прикреплено {ECO_FIELD_FILES_MAX} файлов — больше нельзя
            </span>
          ) : null}
        </div>
      )}
    </Field>
  )
}
